import { useContext, useEffect, useState } from 'react';
import { Search, ChevronDown, ChevronUp } from 'react-feather'
import { useRemote, useStorage } from '../../../Utils/Utils';
import { format } from 'date-fns';
import { BannerContext } from '../../../InternalApiApp';
import { toastDanger } from '../../../components/popups/Toast';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Table from '../../../components/Table/Table';
import SearchField from '../../../components/Input/SearchField';
import ActionButtonsBanner from '../../Shared/Banner/ActionButtonsBanner';
import TruncatedString from '../../Shared/TruncatedString';


export const dropdownIndicator = (props) => {
    return (
        <ChevronDown
            size={18}
            className="dropdown-chevron"
            style={{
                transition: "transform 0.2s",
                transform: props?.selectProps?.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)"
            }}
        />
    )
};

const ExternalAccessUsersList = ({ }) => {
    const remote = useRemote();
    const storage = useStorage();
    const { setBannerBreadCrumbs } = useContext(BannerContext);

    const [loading, setLoading] = useState(true)
    const [editMode, setEditMode] = useState(false)
    const [data, setData] = useState([])
    const [originalData, setOriginalData] = useState([])
    const [editedUsers, setEditedUsers] = useState([])
    const [roleOptions, setRoleOptions] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [sortingColumn, setSortingColumn] = useState({ column: "Name", ascending: true })

    const noChangesMade = JSON.stringify(data) === JSON.stringify(originalData);

    useEffect(() => {
        storage.getOrFetch("/roles/find-role/External Editor").then((roles) => {
            remote.get("/crm/ext/access/lists/users").then(usersList => {
                const externalRole = roles.filter(r => r.displayName === "External Manager")
                const options = externalRole.length > 0 ? [...externalRole, { displayName: "None", id: null, companyId: externalRole[0].companyId }]
                    .map((role) => { return { label: role.displayName, value: role.id } }) : [];

                setRoleOptions(options)
                setBannerBreadCrumbs({ tab: 'Users' })
                setOriginalData(usersList)
                setData(usersList)
                setLoading(false)

            }).catch(() => {
                setData([])
                setLoading(false)
            })
        })
    }, [])

    function filter(dataToFilter) {
        if (searchTerm === null || searchTerm.trim().size === 0) {
            return dataToFilter
        }
        return dataToFilter.filter((datum) => filterBy(datum, ["name", "email", "externalAccessName"], searchTerm))
    }

    function toggleSort(column) {
        if (column === null) {
            setSortingColumn({ column: "Name", ascending: true })
            return
        }
        const ascending = sortingColumn.column === column ? !sortingColumn.ascending : true
        setSortingColumn({ column: column, ascending: ascending })
    }

    function sortTable(dataToSort) {
        if (sortingColumn.column === null) {
            return dataToSort
        }

        let tableData = dataToSort
        const ascending = sortingColumn.ascending

        switch (sortingColumn.column) {
            case "Name":
                tableData.sort((a, b) => {
                    if (!a.name) return 1;
                    if (!b.name) return -1;
                    return ascending ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
                });
                break;
            case "Company":
                tableData.sort((a, b) => {
                    if (!a.externalAccessName) return 1;
                    if (!b.externalAccessName) return -1;
                    return ascending ? a.externalAccessName.localeCompare(b.externalAccessName) : b.externalAccessName.localeCompare(a.externalAccessName);
                });
                break;
            case "Email":
                tableData.sort((a, b) => {
                    if (!a.email) return 1;
                    if (!b.email) return -1;
                    return ascending ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email)
                });
                break;
            case "Last Login":
                tableData.sort((a, b) => {
                    if (!a.lastLogin) return 1;
                    if (!b.lastLogin) return -1;
                    return ascending ? a.lastLogin.localeCompare(b.lastLogin) : b.lastLogin.localeCompare(a.lastLogin)
                });
                break;
            case "Access":
                tableData.sort((a, b) => {
                    const access1 = a.roleId ? "External Manager" : "None"
                    const access2 = b.roleId ? "External Manager" : "None"
                    return ascending ? access1.localeCompare(access2) : access2.localeCompare(access1)
                });
                break;
            default:
                break;
        }
        return tableData
    }

    function handleAccessChange(users, email, newValue) {
        const updatedUsers = users.map((u, index) => {
            if (u.email === email) {
                const updatedUser = { ...u, roleId: newValue.value, roleDTO: !newValue.value ? null : { displayName: newValue.label, roleId: newValue.value } };
                if (originalData[index].roleId !== updatedUser.roleId) {
                    setEditedUsers(editedUsers ? [...editedUsers, updatedUser] : [updatedUser]);
                } else {
                    setEditedUsers(editedUsers.filter(eu => eu.email !== email));
                }
                return updatedUser;
            }
            return u;
        })
        setData(updatedUsers);
    }

    function saveChanges() {
        const updatePromises = editedUsers.map((user) => {
            return remote.put(`/crm/users/${user.email}/`, user)
        });
        Promise.all(updatePromises).then((resp) => {
            console.log("#### Update responses:", resp);
            setEditMode(false);
            setEditedUsers([]);
            setOriginalData(data);
        }).catch(() => {
            toastDanger("Failed to save changes")
            setData(originalData)
            setEditMode(false);
            setEditedUsers([]);
        })
    }

    function cancelChanges() {
        setData(originalData);
        setEditMode(false);
    }

    const filteredData = filter(data)
    const sortedData = sortTable(filteredData)

    const colWidths = ['24%', '20%', '20%', '18%', '18%']
    const headers = [
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Name")} >
                    Name
                    {sortingColumn && sortingColumn.column === "Name" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Name" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Company")} >
                    Company
                    {sortingColumn && sortingColumn.column === "Company" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Company" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Email")} >
                    Email
                    {sortingColumn && sortingColumn.column === "Email" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Emai" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Last Login")} >
                    Last Login
                    {sortingColumn && sortingColumn.column === "Last Login" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Last Login" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        },
        {
            text: (
                <div className='sorting-header' onClick={() => toggleSort("Access")} >
                    Access
                    {sortingColumn && sortingColumn.column === "Access" && sortingColumn.ascending === true ?
                        <ChevronUp className="colour-text-primary" size={12} /> :
                        <ChevronDown className={sortingColumn && sortingColumn.column === "Access" ? "colour-text-primary" : "colour-text-neutral-dark-30"} size={12} />
                    }
                </div>
            )
        }
    ];

    const dataMap = (dataToMap) => {
        return dataToMap.map((user, index) => {
            const { externalAccessName, name, email, lastLogin, roleId } = user
            const access = roleId ? "External Manager" : "None"
            return (
                <tr className='tr-borders cursor-pointer' key={index + "-"+ name}>
                    <td style={{ paddingLeft: '0.5rem' }}>{name || '-'}</td>
                    <td>{externalAccessName || '-'}</td>
                    <td><TruncatedString string={email || '-'} /></td>
                    <td>{lastLogin ? format(new Date(lastLogin), 'HH:mmaaa dd/MM/yyyy') : '-'}</td>
                    <td>{editMode
                        ? <SearchField
                            value={roleId ? roleOptions[0] : roleOptions[1]}
                            options={roleOptions}
                            onChange={(newValue) => handleAccessChange(dataToMap, email, newValue)}
                            size='small'
                            indicatorSeparator={dropdownIndicator}
                        />
                        : access}</td>
                </tr>
            )
        })
    }


    return (
        <Page fixedBanner>
            <PageContentBanner divider gap='m'>
                <SimpleSearchFilter
                    size='small'
                    width='xxl'
                    placeholder={`Search User (${data.length})`}
                    isClearable
                    icon={<Search />}
                    setSearchTerm={setSearchTerm}
                    searchTerm={searchTerm}
                />
                <ActionButtonsBanner
                    hideBackButton
                    editMode={editMode}
                    edit={{ onClick: () => setEditMode(true) }}
                    save={{ onClick: () => saveChanges(), disabled: noChangesMade }}
                    cancel={{ onClick: () => cancelChanges() }}
                />
            </PageContentBanner>
            <PageContentBodyGrid id="client-details-page" rowGap="xl" gridTemplateRows="1fr" splitScroll paddingBottom='4.5rem'>
                <PageGridItem container col="1 / span 12">
                    <Table
                        tableStyle={{ maxWidth: 1400 }}
                        minWidth={1000}
                        colWidths={colWidths}
                        headers={headers}
                        dataMap={dataMap(sortedData)}
                        dataLoading={loading}
                    />
                </PageGridItem>
            </PageContentBodyGrid >
        </Page >
    )
}

export default ExternalAccessUsersList;